import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { CheatSheetButton } from '../components/CheatSheets';
import { SafetyNote } from '@/components/SafetyNote';
import { SAFETY_NOTES } from '@/lib/disclaimers';

type TearDirection = 'bullet-hole' | 'tail-high' | 'tail-low' | 'tail-left' | 'tail-right' | 'diagonal' | 'random';

interface PaperTuneData {
  distance?: number;
  currentTear?: TearDirection;
  /** Iteration counter so users can track repeated paper-tune sessions. */
  iteration?: number;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 7 — Paper Tune
 *
 * Shoot a fletched arrow through butcher paper at 6-8 ft. Read the tear,
 * apply the right adjustment, re-shoot. Always vertical before horizontal.
 */
export function PaperTuneStep({ tuning }: Props) {
  const data = (tuning.session.stepData['paper_tune'] as PaperTuneData | undefined) ?? {};
  const update = (patch: Partial<PaperTuneData>) =>
    tuning.updateStepData<PaperTuneData>('paper_tune', { ...data, ...patch });

  return (
    <>
      <SafetyNote severity={SAFETY_NOTES.paperTune.severity} title={SAFETY_NOTES.paperTune.title}>
        {SAFETY_NOTES.paperTune.body}
      </SafetyNote>

      <GoalCard title="What we're trying to achieve">
        A perfect bullet hole through paper at 6-8 ft means the bow is launching the arrow
        straight — no yaw or pitch from a misaligned rest, off-timed cams, or pinched nock.
        Always fix VERTICAL tears (tail-high / tail-low) before HORIZONTAL.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Setup</h3>
          <CheatSheetButton defaultSheet="paper-tear" label="Paper tear chart" />
        </div>
        <ol className="list-decimal list-inside space-y-1 text-[13px] text-zinc-300 leading-relaxed">
          <li>Stretch butcher paper or newsprint across a frame (24" x 24" minimum).</li>
          <li>Place frame 6-8 ft from shooting position with a backstop 6-8 ft behind it.</li>
          <li>Shoot ONE fletched arrow through the paper. The tear reveals what's off.</li>
          <li>Read the tear, apply the correct adjustment, shoot again. Iterate.</li>
        </ol>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Field label="Distance to paper (ft)">
            <input
              type="number"
              min={4}
              max={15}
              step={0.5}
              value={data.distance ?? ''}
              onChange={(e) => update({ distance: parseFloatOrUndef(e.target.value) })}
              placeholder="6-8"
              className={inputClass}
            />
          </Field>
          <Field label="Iteration #">
            <input
              type="number"
              min={1}
              step={1}
              value={data.iteration ?? ''}
              onChange={(e) => update({ iteration: parseFloatOrUndef(e.target.value) })}
              placeholder="1, 2, 3…"
              className={inputClass}
            />
          </Field>
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">What does your tear look like?</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <Choice
            label="✓ Bullet hole — clean round hole"
            selected={data.currentTear === 'bullet-hole'}
            onClick={() => update({ currentTear: 'bullet-hole' })}
          />
          <Choice
            label="↑ Tail-HIGH (nock above point)"
            selected={data.currentTear === 'tail-high'}
            onClick={() => update({ currentTear: 'tail-high' })}
          />
          <Choice
            label="↓ Tail-LOW (nock below point)"
            selected={data.currentTear === 'tail-low'}
            onClick={() => update({ currentTear: 'tail-low' })}
          />
          <Choice
            label="← Tail-LEFT (RH shooter)"
            selected={data.currentTear === 'tail-left'}
            onClick={() => update({ currentTear: 'tail-left' })}
          />
          <Choice
            label="→ Tail-RIGHT (RH shooter)"
            selected={data.currentTear === 'tail-right'}
            onClick={() => update({ currentTear: 'tail-right' })}
          />
          <Choice
            label="↗ Diagonal (vertical + horizontal)"
            selected={data.currentTear === 'diagonal'}
            onClick={() => update({ currentTear: 'diagonal' })}
          />
          <Choice
            label="? Random / inconsistent"
            selected={data.currentTear === 'random'}
            onClick={() => update({ currentTear: 'random' })}
          />
        </div>
      </Card>

      {data.currentTear === 'bullet-hole' && (
        <Status ok>
          Perfect — your bow is launching arrows straight. Move on to bareshaft to verify
          the dynamic tune, then walk-back for distance confirmation.
        </Status>
      )}

      {data.currentTear === 'tail-high' && (
        <TipCard title="Fix: tail-HIGH tear" defaultOpen>
          <p className="mb-2">
            Nock is too HIGH (or rest is too LOW). Arrow is kicking the rest at release.
          </p>
          <p className="font-semibold text-zinc-100 mb-1">Fix order:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the nocking point DOWN 1/32" — re-shoot.</li>
            <li>If still tail-high, raise the rest 1/32" instead.</li>
            <li>If still tail-high after both: check cam timing — top cam may be hitting early.</li>
          </ol>
        </TipCard>
      )}

      {data.currentTear === 'tail-low' && (
        <TipCard title="Fix: tail-LOW tear" defaultOpen>
          <p className="mb-2">
            Nock is too LOW (or rest is too HIGH). Less common than tail-high.
          </p>
          <p className="font-semibold text-zinc-100 mb-1">Fix order:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the nocking point UP 1/32" — re-shoot.</li>
            <li>If still tail-low, lower the rest 1/32".</li>
            <li>Check tiller — positive tiller (top tighter) can cause persistent tail-low.</li>
          </ol>
        </TipCard>
      )}

      {data.currentTear === 'tail-left' && (
        <TipCard title="Fix: tail-LEFT tear (RH shooter)" defaultOpen>
          <p className="mb-2">
            Arrow is flexing right at release. Could be spine too stiff, cam lean right, or
            rest too far OUT from riser.
          </p>
          <p className="font-semibold text-zinc-100 mb-1">Fix order:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the rest LEFT 1/32" (toward riser) — re-shoot.</li>
            <li>If problem persists, yoke-tune: twist LEFT yoke, untwist RIGHT yoke (single-leg).</li>
            <li>If still off, your spine may be too STIFF. Verify with bareshaft tune.</li>
          </ol>
        </TipCard>
      )}

      {data.currentTear === 'tail-right' && (
        <TipCard title="Fix: tail-RIGHT tear (RH shooter)" defaultOpen>
          <p className="mb-2">
            Arrow is flexing left at release. Could be spine too weak, cam lean left, or
            rest too far IN to riser.
          </p>
          <p className="font-semibold text-zinc-100 mb-1">Fix order:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the rest RIGHT 1/32" (away from riser) — re-shoot.</li>
            <li>If problem persists, yoke-tune: twist RIGHT yoke, untwist LEFT yoke.</li>
            <li>If still off, spine may be too WEAK — common with heavy points + short arrows.</li>
          </ol>
        </TipCard>
      )}

      {data.currentTear === 'diagonal' && (
        <TipCard title="Fix: diagonal tear" defaultOpen>
          <p className="mb-2">
            Two problems at once. Fix VERTICAL component first — the diagonal usually
            resolves on its own once vertical is dialed.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Read the vertical component (high or low) and apply that fix only.</li>
            <li>Re-shoot. If a residual horizontal tear remains, fix it next.</li>
            <li>Never chase both at once — you'll wash one with the other.</li>
          </ol>
        </TipCard>
      )}

      {data.currentTear === 'random' && (
        <TipCard title="Random tears — usually form, not the bow" defaultOpen>
          <p className="mb-2">Inconsistent tears almost never come from a mis-tuned bow. Check:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Vane contact — spray rest + cables with foot powder, shoot 1 arrow, look for streaks.</li>
            <li>Grip — torquing the grip differently each shot causes random tears.</li>
            <li>Peep timing — peep rotating inconsistently throws release timing.</li>
            <li>Try a different arrow (your test arrow may be damaged).</li>
          </ol>
        </TipCard>
      )}

      <InfoCard title="Why 6-8 ft (not closer or further)?">
        <p>
          At &lt; 6 ft the arrow hasn't yet stabilized — even a perfectly tuned bow shows
          minor tail wobble that close. At &gt; 10 ft the fletching has had time to correct
          flight, masking the underlying nock travel issues we're trying to read. 6-8 ft is
          the sweet spot.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Backstop matters — bales of straw, foam, archery netting. Never shoot through paper
        with a hard backstop too close behind; bounce-back is real. Maintain 8+ ft between
        paper and a soft target.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[11px] text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div className={`text-sm leading-relaxed p-2.5 rounded border ${ok ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300' : 'border-amber-700/40 bg-amber-950/20 text-amber-300'}`}>
      {children}
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
